/* New gangsheet (0.3e): the button (top of the tool rail; a + in the phone top bar) and the two questions
   it may need to ask before starting fresh. The flow itself is startFresh() in the store, shared with the
   ⋯ menu and My gang sheets. */

import { useState } from 'react';
import { useStore, startFresh, cancelStartFresh, toast } from '../store/store';
import { Dialog } from '../ui/primitives';
import { Icon } from '../ui/icons';

/** The New gangsheet button. `variant`: the tool rail (desktop and tablet) or the phone top bar. */
export function NewGangsheetButton({ variant }: { variant: 'rail' | 'top' }) {
  const go = () => void startFresh();
  if (variant === 'top') {
    return (
      <button type="button" className="ibtn top-new only-phone" aria-label="New gangsheet" data-new-gangsheet onClick={go}>
        <Icon name="plus" />
      </button>
    );
  }
  return (
    <button type="button" className="rail-btn rail-new" aria-label="New gangsheet" data-tip="Start Fresh" data-tip-pos="right" data-new-gangsheet onClick={go}>
      <span className="rail-ico"><Icon name="plus" /></span>
      <span className="rail-label">New<br />gangsheet</span>
    </button>
  );
}

const time = (t: number) => new Date(t).toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit' });

/** What New gangsheet asks when it can't go straight on. Mounted once for the whole app. */
export function StartFreshPrompt() {
  const p = useStore(s => s.freshPrompt);
  const [busy, setBusy] = useState(false);
  if (!p) return null;
  const name = p.name || 'your gangsheet';

  if (p.kind === 'uploading') {
    const n = p.count;
    return (
      <Dialog title="Files still uploading" onClose={cancelStartFresh} className="fresh-dlg">
        <span className="kicker">New gangsheet</span>
        <h2>{n === 1 ? 'A file is' : n + ' files are'} still uploading</h2>
        <p className="muted">{n === 1 ? 'It’s' : 'They’re'} being added to “{name}”. Start fresh once {n === 1 ? 'it’s' : 'they’re'} in, so nothing is lost.</p>
        <div className="dialog-actions">
          <button className="btn btn-line btn-sm" onClick={cancelStartFresh}>Keep editing</button>
          <button className="btn btn-primary btn-sm" data-fresh="wait" onClick={() => {
            toast('Starting fresh as soon as your uploads are in.');
            void startFresh({ over: p.over, waitForUploads: true });
          }}>Start fresh when they’re in</button>
        </div>
      </Dialog>
    );
  }

  const run = async (discard: boolean) => {
    setBusy(true);
    try { await startFresh({ over: p.over, discard }); } finally { setBusy(false); }
  };
  return (
    <Dialog title="Latest changes not saved" onClose={busy ? undefined : cancelStartFresh} className="fresh-dlg">
      <span className="kicker">New gangsheet</span>
      <h2>Your latest changes aren’t saved yet</h2>
      <p className="muted">
        We couldn’t save “{name}” just now{p.savedAt ? <>. It still has the version saved at {time(p.savedAt)}</> : null}.
        Check your internet connection, then try again.
      </p>
      <div className="callout callout-warn" style={{ marginTop: 12 }}>
        <Icon name="alert" />
        <div>Start fresh anyway and the changes {p.savedAt ? 'since ' + time(p.savedAt) : 'since it was last saved'} are lost. Everything saved before stays in My gang sheets.</div>
      </div>
      <div className="dialog-actions fresh-acts">
        <button className="btn btn-line btn-sm" disabled={busy} onClick={cancelStartFresh}>Keep editing</button>
        <button className="btn btn-danger btn-sm" disabled={busy} data-fresh="discard" onClick={() => void run(true)}>Start fresh anyway</button>
        <button className="btn btn-primary btn-sm" disabled={busy} data-fresh="retry" onClick={() => void run(false)}>{busy ? <span className="spin" /> : <Icon name="redo" />}Try again</button>
      </div>
    </Dialog>
  );
}
